import React from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { Card } from '@theneatcompany/nui'
import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'

import { getNewsletterPosts, getNewsletterYearMonth } from '@/lib/blog/newsletter'
import { Layout, Section } from '@/components/layout'
import { formatBlogDate } from '@/utils/date'
import Link from '@/components/common/Link'
import YearMonthAccordion from '@/components/common/YearMonthAccordion'
import NewsletterHeader from '@/components/blocks/NewsletterHeader'

import styles from './newsletter-archive.module.scss'

const pageId = 'newsletterArchive'
const getMessageId = id => `pages.newsletter.${id}`

function groupByYearMonth(posts) {
  return posts.reduce((acc, post) => {
    const date = new Date(post.date)
    const year = date.getFullYear()
    const month = format(date, 'LLLL', { locale: enUS })
    if (!acc[year]) acc[year] = {}
    // Posts come from WordPress newest-first; first per month wins
    if (!acc[year][month]) acc[year][month] = post
    return acc
  }, {})
}

export default function NewsletterArchive({ data }) {
  const { formatMessage } = useIntl()

  const renderPostContent = (post) => (
    <div className={styles.postContent}>
      {post.featuredImage?.node?.sourceUrl && (
        <img
          alt={post.featuredImage.node.altText || post.title}
          className={styles.postImage}
          src={post.featuredImage.node.sourceUrl}
        />
      )}
      <h3 className={styles.postTitle}>{post.title}</h3>
      <p className={styles.postDate}>{formatBlogDate(new Date(post.date))}</p>
      <div className={styles.postExcerpt} dangerouslySetInnerHTML={{ __html: post.excerpt }} />
      <Link to={`/newsletter/archive/${getNewsletterYearMonth(post)}`}>
        {formatMessage({ id: getMessageId('archive.readMore') })}
      </Link>
    </div>
  )

  return (
    <Layout.Content>
      <Section bgBlob>
        <NewsletterHeader title={formatMessage({ id: getMessageId('archive.title') })} />
      </Section>
      <Section paddingTop={18}>
        <Card className={styles.card}>
          <YearMonthAccordion
            className={styles.accordions}
            data={data}
            monthClassName={styles.monthAccordion}
            renderContent={renderPostContent}
          />
        </Card>
      </Section>
    </Layout.Content>
  )
}

NewsletterArchive.propTypes = {
  data: PropTypes.objectOf(
    PropTypes.objectOf(
      PropTypes.shape({
        slug: PropTypes.string.isRequired,
        date: PropTypes.string.isRequired,
        excerpt: PropTypes.string,
        title: PropTypes.string,
        featuredImage: PropTypes.shape({
          node: PropTypes.shape({
            sourceUrl: PropTypes.string,
            altText: PropTypes.string,
          }),
        }),
      }),
    ),
  ).isRequired,
}

NewsletterArchive.layoutProps = { pageId }

export async function getStaticProps() {
  const posts = await getNewsletterPosts()
  const data = groupByYearMonth(posts)

  return {
    props: { data },
    revalidate: 60 * 60,
  }
}
